import { Button } from "primereact/button";
import { motion } from "framer-motion";
import * as client from "../../client";
import styles from "./Comment.module.css";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import {
  useAddComment,
  useLocalUser,
  useUsers,
} from "../../pages/specialty-assistant/data";
import { PantheonAvatarWithoutMenu } from "../avatar/Avatar";
import { displayNameForUser } from "../../util";
import React from "react";

type CommentContainerProps = {
  reportName: string;
  reportId: string;
  comments: client.Comments;
};

export const CommentContainer: React.FC<CommentContainerProps> = ({
  reportName,
  reportId,
  comments,
}) => {
  const containerVariants = {
    rest: {},
    hover: { scale: 1.05, boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)" },
  };

  const overlayPanel = useRef<OverlayPanel>(null);
  const [newComment, setNewComment] = useState("");
  const allComments = comments.all_comments;
  const addComment = useAddComment(reportId);
  const users = useUsers();
  const localUser = useLocalUser();

  const onAddComment = async () => {
    if (newComment) {
      await addComment.mutateAsync(newComment);
      setNewComment("");
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      className={styles.buttonContainer}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.2 }}
      initial="rest"
      whileHover="hover"
      style={allComments.length === 0 ? { width: "28px" } : {}}
    >
      <Button
        className={styles.mainButton}
        icon="pi pi-comment"
        size="small"
        severity="secondary"
        text
        onClick={(e) => {
          if (overlayPanel.current) {
            overlayPanel.current.toggle(e);
          }
        }}
      >
        {allComments.length > 0 && (
          <Badge value={allComments.length} severity="info" />
        )}
        <OverlayPanel
          ref={overlayPanel}
          showCloseIcon
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            marginLeft: "20px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.commentListContainer}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                marginBottom: "12px",
              }}
            >
              {`${reportName} Comments`}
            </div>
            {allComments.map((comment) => {
              const user = users.data?.all_company_users.find(
                (user) => user.id === comment.user_id
              );
              const postTime = new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
              }).format(new Date(comment.created_at_ts));
              return (
                <React.Fragment key={comment.id}>
                  <div className={styles.horizontalContainer}>
                    <PantheonAvatarWithoutMenu
                      label={displayNameForUser(user)}
                      showName={true}
                    />
                    <div className={styles.timeText}>{postTime}</div>
                  </div>
                  <div className={styles.commentMessage}>{comment.message}</div>
                </React.Fragment>
              );
            })}
            <div className={styles.horizontalContainer}>
              <PantheonAvatarWithoutMenu
                label={displayNameForUser(localUser)}
              />
              <InputTextarea
                placeholder="Add a comment..."
                rows={1}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    newComment &&
                    e.key == "Enter" &&
                    (e.shiftKey || e.metaKey || e.ctrlKey)
                  ) {
                    onAddComment();
                  }
                }}
                value={newComment}
                style={{ flexGrow: "1", margin: "2px" }}
                disabled={addComment.isPending}
                autoResize
              />
              <Button
                loading={addComment.isPending}
                disabled={!newComment || addComment.isPending}
                icon="pi pi-arrow-circle-up"
                onClick={onAddComment}
                style={{ height: "28px", width: "28px" }}
              />
            </div>
          </div>
        </OverlayPanel>
      </Button>
    </motion.div>
  );
};
